
import React from 'react';
import { Card } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Button } from '@/components/ui/button';
import { AlertTriangle, Hash, Star } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

interface AdminGamesSelectHeaderProps {
  selectedGames: string[];
  games: any[];
  totalGames: number;
  onSelectAll: () => void;
  onSelectAllPages: () => void;
  onSelectAllUnconfigured?: () => void;
  onSelectAllUnconfiguredPlayCount?: () => void;
  onSelectAllUnconfiguredRatings?: () => void;
  onClearSelection: () => void;
}

const AdminGamesSelectHeader: React.FC<AdminGamesSelectHeaderProps> = ({
  selectedGames,
  games,
  totalGames,
  onSelectAll,
  onSelectAllPages,
  onSelectAllUnconfigured,
  onSelectAllUnconfiguredPlayCount,
  onSelectAllUnconfiguredRatings,
  onClearSelection
}) => {
  const allCurrentPageSelected = selectedGames.length === games.length && games.length > 0;
  const someSelected = selectedGames.length > 0;

  return (
    <Card className="p-4">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-4">
          <Checkbox
            checked={allCurrentPageSelected}
            onCheckedChange={onSelectAll}
            className="mr-2"
          />
          <span className="text-sm font-medium text-gray-700">
            Select All on Page ({games.length})
          </span>
        </div>
        
        {someSelected && (
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm text-blue-600 font-medium">
              {selectedGames.length} game(s) selected
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={onSelectAllPages}
              className="text-blue-600 border-blue-200 hover:bg-blue-50"
            >
              Select All {totalGames.toLocaleString()} Games
            </Button>
            
            {/* Unconfigured Games Dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                  className="text-orange-600 border-orange-200 hover:bg-orange-50 flex items-center gap-2"
                >
                  <AlertTriangle className="w-4 h-4" />
                  Select Unconfigured
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {onSelectAllUnconfigured && (
                  <DropdownMenuItem onClick={onSelectAllUnconfigured}>
                    <AlertTriangle className="w-4 h-4 mr-2" />
                    All Unconfigured Games
                  </DropdownMenuItem>
                )}
                <DropdownMenuSeparator />
                {onSelectAllUnconfiguredPlayCount && (
                  <DropdownMenuItem onClick={onSelectAllUnconfiguredPlayCount}>
                    <Hash className="w-4 h-4 mr-2" />
                    Missing Play Count
                  </DropdownMenuItem>
                )}
                {onSelectAllUnconfiguredRatings && (
                  <DropdownMenuItem onClick={onSelectAllUnconfiguredRatings}>
                    <Star className="w-4 h-4 mr-2" />
                    Missing Ratings
                  </DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
            
            <Button
              variant="outline"
              size="sm"
              onClick={onClearSelection}
            >
              Clear Selection
            </Button>
          </div>
        )}
        
        {!someSelected && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="outline"
                size="sm"
                className="text-orange-600 border-orange-200 hover:bg-orange-50 flex items-center gap-2"
              >
                <AlertTriangle className="w-4 h-4" />
                Select Unconfigured Games
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {onSelectAllUnconfigured && (
                <DropdownMenuItem onClick={onSelectAllUnconfigured}>
                  <AlertTriangle className="w-4 h-4 mr-2" />
                  All Unconfigured Games
                </DropdownMenuItem>
              )}
              <DropdownMenuSeparator />
              {onSelectAllUnconfiguredPlayCount && (
                <DropdownMenuItem onClick={onSelectAllUnconfiguredPlayCount}>
                  <Hash className="w-4 h-4 mr-2" />
                  Missing Play Count
                </DropdownMenuItem>
              )}
              {onSelectAllUnconfiguredRatings && (
                <DropdownMenuItem onClick={onSelectAllUnconfiguredRatings}>
                  <Star className="w-4 h-4 mr-2" />
                  Missing Ratings
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>
    </Card>
  );
};

export default AdminGamesSelectHeader;
